import { useEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, animate, motion, useInView, useReducedMotion } from "motion/react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { api, useApi } from "../api.ts";
import { useAuth } from "../auth.tsx";
import { Button, cn, ErrorText, Icon, LoadError, Loading, useToast } from "../ui/index.tsx";
import { CompatBadges, DetectionBadge, GameArt, NotForSale, startCheckout, useStripeEnabled } from "./_shared.tsx";
import { PublicHeading } from "./Games.tsx";
import type { Bundle, Game, PublicOffer } from "../../shared/types.ts";
import { fmtRelative } from "../format.ts";

/** "Lifetime" or the longest duration is highlighted as best value. */
const bestIndex = (g: Game) => {
  const life = g.pricing_tiers.findIndex((t) => /lifetime/i.test(t.dur));
  return life >= 0 ? life : g.pricing_tiers.length - 1;
};

/** "£4.99" -> { prefix: "£", value: 4.99, decimals: 2, suffix: "" }; null when there's no number to animate. */
function splitPrice(price: string) {
  const m = /^(\D*?)(\d+(?:[.,]\d+)?)(.*)$/.exec(price.trim());
  if (!m) return null;
  const digits = m[2]!.replace(",", ".");
  return { prefix: m[1]!, value: Number(digits), decimals: digits.includes(".") ? digits.split(".")[1]!.length : 0, suffix: m[3]! };
}

/** Days in a tier like "1 Day", "2 Weeks", "3 Months"; null for lifetime or anything we can't read. */
function tierDays(dur: string): number | null {
  const m = /(\d+)\s*(day|week|month|year)/i.exec(dur);
  if (!m) return null;
  return Number(m[1]) * { day: 1, week: 7, month: 30, year: 365 }[m[2]!.toLowerCase() as "day"];
}

/** Counts the price up from zero the first time it scrolls into view. Text is always the real price for screen readers. */
function Price({ price }: { price: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const seen = useInView(ref, { once: true, margin: "0px 0px -15% 0px" });
  const calm = useReducedMotion();
  const parts = splitPrice(price);
  useEffect(() => {
    const el = ref.current;
    if (!el || !parts || !seen || calm) return;
    const c = animate(0, parts.value, { duration: 0.9, ease: [0.2, 0.8, 0.2, 1], onUpdate: (v) => { el.textContent = `${parts.prefix}${v.toFixed(parts.decimals)}${parts.suffix}`; } });
    return () => c.stop();
  }, [seen, price, calm]);
  return <span ref={ref} aria-label={price}>{price}</span>;
}

function PerDay({ price, dur }: { price: string; dur: string }) {
  const parts = splitPrice(price);
  const days = tierDays(dur);
  const text = /lifetime/i.test(dur) ? "one payment, yours to keep" : parts && days && days > 1 ? `${parts.prefix}${(parts.value / days).toFixed(2)} a day` : null;
  if (!text) return <span className="h-4" />;
  // Revealed on hover; always shown where there's no hover (touch screens).
  return <span className="h-4 translate-y-1 text-xs text-muted opacity-0 transition-all duration-300 group-hover/tier:translate-y-0 group-hover/tier:opacity-100 [@media(hover:none)]:translate-y-0 [@media(hover:none)]:opacity-100">{text}</span>;
}

// Every answer here describes how the platform actually behaves; keep it that way when editing.
const FAQ: { q: string; a: ReactNode }[] = [
  { q: "How fast do I get access?", a: <>Straight away. As soon as the payment clears, the game appears on your account and in the loader. No waiting for someone to activate it.</> },
  { q: "What happens if I buy more time?", a: <>It stacks. Extra time is added on top of whatever you have left, so extending early never wastes a day. Lifetime never expires.</> },
  { q: "How do I pay?", a: <>By card through Stripe on our own checkout page. We never see or store your card details.</> },
  { q: "Can I get a refund?", a: <>Refunds are only given when there's a genuine fault with the product that we can't fix, and they're at our discretion. If something isn't working, open a ticket first: most problems are sorted quickly.</> },
  { q: "Do I need a separate download per game?", a: <>No. One loader covers every game on your account, and it updates itself when we ship a new build. See the <Link to="/changelog" className="text-fg underline decoration-line-strong underline-offset-2 hover:decoration-accent">changelog</Link> for what changed.</> },
  { q: "I got a new PC. Can I move my license?", a: <>Yes. Reset your hardware lock yourself from the Account page, no ticket needed. There's a short cooldown between resets.</> },
  { q: "How do I know a game is safe to use right now?", a: <>Every game shows its live detection state, and the <Link to="/status" className="text-fg underline decoration-line-strong underline-offset-2 hover:decoration-accent">status page</Link> has 90 days of history plus any incident we're working on.</> },
  { q: "Something isn't working. Where do I get help?", a: <>Open a ticket from Support once you're signed in, or ask in our Discord. Setup guides and an error-code reference are in Guides.</> },
];

function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="mt-20" aria-labelledby="faq-title">
      <div className="kicker mb-3 text-accent">// questions</div>
      <h2 id="faq-title" className="mb-8 text-3xl font-semibold tracking-tight sm:text-4xl">Before you buy</h2>
      <div className="divide-y divide-line overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface/50">
        {FAQ.map((f, i) => {
          const on = open === i;
          return (
            <div key={f.q}>
              <h3>
                <button type="button" aria-expanded={on} aria-controls={`faq-${i}`} onClick={() => setOpen(on ? null : i)}
                  className={cn("flex w-full items-center justify-between gap-4 px-5 py-4 text-left font-medium transition-colors hover:bg-hover/60", on && "text-fg")}>
                  {f.q}
                  <motion.span animate={{ rotate: on ? 45 : 0 }} transition={{ type: "spring", stiffness: 420, damping: 26 }} className={cn("shrink-0", on ? "text-accent" : "text-faint")}>
                    <Icon name="plus" className="size-4" />
                  </motion.span>
                </button>
              </h3>
              <AnimatePresence initial={false}>
                {on && (
                  <motion.div id={`faq-${i}`} role="region" key="a" className="overflow-hidden"
                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}>
                    <p className="max-w-2xl px-5 pb-5 text-sm leading-relaxed text-muted">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/**
 * What the bundle's games would cost bought one by one at the same duration, or null if any game has no matching
 * tier (then we don't claim a saving we can't back up).
 */
function separatePrice(b: Bundle, games: Game[]): { cents: number; prefix: string } | null {
  let cents = 0;
  let prefix = "";
  for (const id of b.games) {
    const g = games.find((x) => x.id === id);
    const tier = g?.pricing_tiers.find((t) => (b.days === null ? /lifetime/i.test(t.dur) : tierDays(t.dur) === b.days));
    const p = tier && splitPrice(tier.price);
    if (!p) return null;
    cents += Math.round(p.value * 100);
    prefix = p.prefix;
  }
  return { cents, prefix };
}

function BundleCard({ b, games, onBuy, busy }: { b: Bundle; games: Game[]; onBuy: () => void; busy: boolean }) {
  const art = b.games.map((id) => games.find((g) => g.id === id)).filter((g): g is Game => !!g);
  const sep = separatePrice(b, games);
  const own = splitPrice(b.price);
  const saving = sep && own ? sep.cents - Math.round(own.value * 100) : 0;
  return (
    <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "0px 0px -10% 0px" }}
      transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }} whileHover={{ y: -4 }}
      className="pane hud glow-border group flex flex-col overflow-hidden">
      <div className="relative grid grid-cols-2 gap-px bg-line">
        {art.slice(0, 4).map((g) => <GameArt key={g.id} game={g} className="aspect-[460/215] [&_img]:transition-transform [&_img]:duration-700 group-hover:[&_img]:scale-[1.05]" />)}
        {saving > 0 && <span className="absolute right-3 top-3 rounded bg-accent px-1.5 text-[10px] font-semibold uppercase leading-5 text-accent-fg">Save {sep!.prefix}{(saving / 100).toFixed(2).replace(/\.00$/, "")}</span>}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="kicker mb-1">{b.days === null ? "Lifetime" : `${b.days} days`} · {b.games.length} games</div>
        <h3 className="text-xl font-semibold tracking-tight">{b.name}</h3>
        {b.description && <p className="mt-1.5 text-sm text-muted">{b.description}</p>}
        <ul className="mt-3 flex flex-wrap gap-1.5">{b.game_names.map((n) => <li key={n} className="rounded-md border border-line bg-bg/60 px-2 py-0.5 text-xs text-muted">{n}</li>)}</ul>
        <div className="mt-auto flex items-end justify-between gap-3 pt-5">
          <div>
            <div className="font-display text-3xl font-semibold tabular-nums tracking-tight"><Price price={b.price} /></div>
            {sep && saving > 0 && <div className="text-xs text-faint"><span className="line-through">{sep.prefix}{(sep.cents / 100).toFixed(2).replace(/\.00$/, "")}</span> bought separately</div>}
          </div>
          <Button variant="primary" onClick={onBuy} loading={busy}>Get bundle</Button>
        </div>
      </div>
    </motion.div>
  );
}

/** Public promo codes, with a copy button. Only codes the admin switched on for the store, and only while they're live. */
function Offers() {
  const offers = useApi<PublicOffer[]>("/offers");
  const toast = useToast();
  const list = offers.data ?? [];
  if (!list.length) return null;
  return (
    <div className="mb-8 space-y-3">
      {list.slice(0, 3).map((o, i) => (
        <motion.div key={o.code} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
          className="glow-border flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-card)] border border-accent/40 bg-accent-soft px-5 py-3.5">
          <div className="flex items-center gap-3">
            <Icon name="gift" className="size-5 text-accent" />
            <div>
              <div className="font-medium"><span className="text-gradient">{o.description}</span> {o.applies_to_names ? o.applies_to_names.join(", ") : "everything"}</div>
              <div className="text-xs text-muted">
                {o.expires_at ? `Ends ${fmtRelative(o.expires_at)}` : "No end date"}{o.uses_left !== null ? ` · ${o.uses_left} left` : ""} · enter it at checkout
              </div>
            </div>
          </div>
          <button type="button" onClick={() => { void navigator.clipboard.writeText(o.code); toast(`${o.code} copied`, "success"); }}
            className="flex items-center gap-2 rounded-lg border border-accent/50 bg-bg/60 px-3 py-1.5 font-mono text-sm font-semibold tracking-wider transition-colors hover:bg-bg">
            {o.code}<Icon name="copy" className="size-3.5 text-accent" />
          </button>
        </motion.div>
      ))}
    </div>
  );
}

/** Art on the left, name and badges on top, the caller's content below. */
function GameRow({ g, children }: { g: Game; children: ReactNode }) {
  return (
    <motion.div id={g.id} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "0px 0px -10% 0px" }}
      transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }} className="pane hud group scroll-mt-28 overflow-hidden md:flex">
      <div className="relative md:w-72 md:shrink-0">
        <GameArt game={g} className="aspect-[460/215] md:aspect-auto md:h-full [&_img]:transition-transform [&_img]:duration-700 [&_img]:ease-out group-hover:[&_img]:scale-[1.05]" />
        <div className="absolute inset-0 bg-gradient-to-t from-surface/90 to-transparent md:bg-gradient-to-r md:from-transparent md:to-surface" />
      </div>
      <div className="min-w-0 flex-1 p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold tracking-tight">{g.name}</h2>
          <div className="flex flex-wrap items-center gap-2">
            <Link to={`/games/${g.id}`} className="mr-1 text-sm text-muted hover:text-fg">Details</Link>
            <CompatBadges game={g} />
            <DetectionBadge status={g.detection_status} />
          </div>
        </div>
        {children}
      </div>
    </motion.div>
  );
}

export default function Pricing() {
  const games = useApi<Game[]>("/games");
  const { me } = useAuth();
  const stripe = useStripeEnabled();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const listed = (games.data ?? []).filter((g) => g.status !== "discontinued");
  const priced = listed.filter((g) => g.purchasable && g.pricing_tiers.length > 0);
  const bundles = useApi<Bundle[]>("/bundles");
  const buyable = (bundles.data ?? []).filter((b) => b.purchasable);
  const buyBundle = (b: Bundle) => navigate(me ? `/checkout/bundle/${b.id}` : `/register?next=${encodeURIComponent(`/checkout/bundle/${b.id}`)}`);
  const soon = listed.filter((g) => !g.purchasable);

  useEffect(() => {
    if (params.get("checkout") === "cancelled") { toast("Checkout cancelled. You weren't charged.", "info"); setParams({}, { replace: true }); }
  }, []);

  useEffect(() => {
    if (!location.hash || !games.data) return;
    document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash, games.data]);

  const buy = async (g: Game, i: number) => {
    if (!me) { navigate(`/register?next=${encodeURIComponent("/pricing#" + g.id)}`); return; }
    setBusy(`${g.id}:${i}`);
    setError(null);
    try {
      if (stripe) await startCheckout(g.id, i);
      else {
        const r = await api.post<{ ticket_id: number }>("/purchase-requests", { game_id: g.id, tier_index: i });
        toast("Purchase request sent", "success");
        navigate(`/support/${r.ticket_id}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  };

  return (
    <>
      <PublicHeading title="Pricing" body={stripe
        ? "Pick a game and a duration. Pay by card and access lands on your account the moment the payment clears."
        : "Pick a game and a duration, from one day to lifetime. Request a purchase and we'll activate it on your account."} />
      <section className="mx-auto max-w-6xl px-6 py-12">
        {stripe && <Offers />}
        <ErrorText>{error}</ErrorText>
        {games.loading && !games.data ? <Loading /> : games.error && !games.data ? <LoadError error={games.error} retry={games.reload} /> : priced.length === 0 && soon.length === 0 ? (
          <p className="rounded-[var(--radius-card)] border border-dashed border-line py-12 text-center text-sm text-muted">Pricing is coming soon.</p>
        ) : (
          <div className="mt-2 space-y-6">
            {stripe && buyable.length > 0 && (
              <section id="bundles" className="scroll-mt-28 pb-6">
                <h2 className="kicker mb-4 text-accent">// bundles</h2>
                <div className="grid gap-5 md:grid-cols-2">{buyable.map((b) => <BundleCard key={b.id} b={b} games={games.data ?? []} onBuy={() => buyBundle(b)} busy={false} />)}</div>
              </section>
            )}
            {priced.map((g) => {
              const best = bestIndex(g);
              return (
                <GameRow key={g.id} g={g}>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {g.pricing_tiers.map((t, i) => (
                      <motion.div key={`${t.dur}-${i}`} whileHover={{ y: -4 }} transition={{ type: "spring", stiffness: 400, damping: 24 }}
                        className={`group/tier relative flex flex-col rounded-lg border p-4 transition-colors ${i === best ? "glow-border border-accent/50 bg-accent-soft" : "border-line bg-bg/70 hover:border-line-strong"}`}>
                        {i === best && <span className="absolute -top-2 right-3 rounded bg-accent px-1.5 text-[10px] font-semibold uppercase leading-4 text-accent-fg">Best value</span>}
                        <span className="kicker">{t.dur}</span>
                        <span className="mt-1 font-display text-3xl font-semibold tabular-nums tracking-tight"><Price price={t.price} /></span>
                        <PerDay price={t.price} dur={t.dur} />
                        <Button size="sm" variant={i === best ? "primary" : "secondary"} className="mt-3" loading={busy === `${g.id}:${i}`} disabled={busy !== null} onClick={() => buy(g, i)}>
                          {!me ? "Get started" : stripe ? "Buy now" : "Request"}
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                </GameRow>
              );
            })}
            {soon.length > 0 && (
              <>
                <h2 className="kicker pt-6 text-info">// coming soon</h2>
                {soon.map((g) => (
                  <GameRow key={g.id} g={g}>
                    <div className="max-w-xl"><NotForSale game={g} /></div>
                    {g.pricing_tiers.length > 0 && (
                      <>
                        <div className="kicker mb-2 mt-5">Launch pricing</div>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                          {g.pricing_tiers.map((t, i) => (
                            <div key={`${t.dur}-${i}`} className="flex flex-col rounded-lg border border-line bg-bg/70 p-4 opacity-60">
                              <span className="kicker">{t.dur}</span>
                              <span className="mt-1 font-display text-3xl font-semibold tabular-nums tracking-tight">{t.price}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </GameRow>
                ))}
              </>
            )}
          </div>
        )}
        <p className="mt-8 text-sm text-faint">
          {stripe ? "Payments are processed by Stripe. We never see your card details." : "Requests open a support ticket; an admin confirms payment and activates your license."}
          {" "}Durations stack: buying more time adds to whatever you have left.
        </p>
        <Faq />
      </section>
    </>
  );
}
